import { LocaleLink } from '@/components/LocaleLink'

import { HomeCardImage } from '@/components/home-editorial/HomeCardImage'
import { CategoryLabel, ReadTime } from '@/components/home-editorial/common'
import {
  HOME_BI_NEWS_IMAGE_HOVER,
  HOME_BI_NEWS_ITEM_GROUP,
  HOME_BI_NEWS_TITLE_LINK,
} from '@/components/home-editorial/home-bi-layout'
import type { DestaquePublicacao } from '@/components/home-editorial/types'
import { cn } from '@/utilities/ui'

/** "01", "02"… (índice 0-based → rótulo 1-based com 2 dígitos). */
const formatIndex = (index: number) => String(index + 1).padStart(2, '0')

export type MostReadSlideProps = {
  item: DestaquePublicacao
  index: number
  showThumbnail?: boolean
  className?: string
}

/**
 * Slide editorial "imagem atrás do número" (fiel ao mock shadcn `mais-lidos`): número grande
 * branco sobre a imagem com gradiente, coluna `border-l`, título em <h3> (SEO), resumo e tempo
 * de leitura. Reutiliza os tokens BI e `HomeCardImage` (next/image) do projeto.
 */
export function MostReadSlide({
  item,
  index,
  showThumbnail = true,
  className,
}: MostReadSlideProps) {
  return (
    <div className={cn('flex h-full flex-col border-l px-6', className)}>
      <article className={cn('flex h-full flex-col', HOME_BI_NEWS_ITEM_GROUP)}>
        {showThumbnail && item.img ? (
          <div className="relative mb-3 h-36 w-full overflow-hidden sm:h-40">
            <HomeCardImage
              src={item.img}
              srcSizes={item.imgSizes}
              alt={item.alt}
              className={HOME_BI_NEWS_IMAGE_HOVER}
              sizes="(min-width: 1024px) 25vw, (min-width: 640px) 52vw, 88vw"
            />
            <div
              className="absolute inset-0 bg-linear-to-r from-black/75 via-black/45 to-black/10"
              aria-hidden
            />
            <span className="absolute inset-y-0 left-0 flex items-end pb-1 pl-2 text-[4rem] font-semibold leading-none tabular-nums text-white">
              {formatIndex(index)}
            </span>
          </div>
        ) : null}
        {item.category ? (
          <CategoryLabel href={item.categoryHref} className="mt-0 mb-1">
            {item.category}
          </CategoryLabel>
        ) : null}
        <h3 className="mt-0 mb-2 text-base font-semibold leading-snug">
          <LocaleLink href={item.href} prefetch={false} className={HOME_BI_NEWS_TITLE_LINK}>
            {item.title}
          </LocaleLink>
        </h3>
        {item.description ? (
          <p className="text-muted-foreground mb-0 text-sm leading-relaxed">{item.description}</p>
        ) : null}
        {item.read ? <ReadTime>{item.read}</ReadTime> : null}
      </article>
    </div>
  )
}
